import { useMemo } from 'react'

interface BoatOrientationProps {
    heading: number   // degrees, 0=N, 90=E, etc.
    pitch: number     // degrees, negative = nose down
    roll: number      // degrees, negative = left
    size?: number
}

export default function BoatOrientation({ heading, pitch, roll, size = 160 }: BoatOrientationProps) {
    // Compass labels
    const compassPoints = useMemo(() => {
        const r = size / 2 - 8
        const cx = size / 2
        const cy = size / 2
        return ['N', 'E', 'S', 'W'].map((label, i) => {
            const angle = (i * 90 - heading) * (Math.PI / 180) - Math.PI / 2
            return {
                label,
                x: cx + r * Math.cos(angle),
                y: cy + r * Math.sin(angle),
                isNorth: label === 'N'
            }
        })
    }, [heading, size])

    // Compass tick marks (every 30°)
    const ticks = useMemo(() => {
        const r1 = size / 2 - 18
        const r2 = size / 2 - 14
        const cx = size / 2
        const cy = size / 2
        return Array.from({ length: 12 }, (_, i) => {
            const deg = i * 30
            const angle = (deg - heading) * (Math.PI / 180) - Math.PI / 2
            const isMajor = deg % 90 === 0
            return {
                x1: cx + (isMajor ? r1 - 2 : r1) * Math.cos(angle),
                y1: cy + (isMajor ? r1 - 2 : r1) * Math.sin(angle),
                x2: cx + r2 * Math.cos(angle),
                y2: cy + r2 * Math.sin(angle),
                isMajor
            }
        })
    }, [heading, size])

    const cx = size / 2
    const cy = size / 2

    // Boat hull shape scaled to fit
    const boatScale = size / 200
    const hullPath = `
        M ${cx} ${cy - 30 * boatScale}
        C ${cx + 8 * boatScale} ${cy - 25 * boatScale}, ${cx + 12 * boatScale} ${cy - 10 * boatScale}, ${cx + 14 * boatScale} ${cy + 5 * boatScale}
        L ${cx + 12 * boatScale} ${cy + 20 * boatScale}
        C ${cx + 10 * boatScale} ${cy + 26 * boatScale}, ${cx + 5 * boatScale} ${cy + 28 * boatScale}, ${cx} ${cy + 30 * boatScale}
        C ${cx - 5 * boatScale} ${cy + 28 * boatScale}, ${cx - 10 * boatScale} ${cy + 26 * boatScale}, ${cx - 12 * boatScale} ${cy + 20 * boatScale}
        L ${cx - 14 * boatScale} ${cy + 5 * boatScale}
        C ${cx - 12 * boatScale} ${cy - 10 * boatScale}, ${cx - 8 * boatScale} ${cy - 25 * boatScale}, ${cx} ${cy - 30 * boatScale}
        Z
    `

    // Keel line (center line of boat)
    const keelPath = `M ${cx} ${cy - 26 * boatScale} L ${cx} ${cy + 26 * boatScale}`

    // Pitch indicator: tilt the 3D perspective
    // Roll indicator: tilt the 3D perspective
    const transform3d = `perspective(${size * 3}px) rotateX(${-pitch * 0.6}deg) rotateY(${roll * 0.6}deg)`

    // Water level indicator based on pitch/roll
    const waterAngle = roll
    const waterY = cy + pitch * 0.3

    return (
        <div className="flex flex-col items-center gap-1">
            <div
                style={{
                    width: size,
                    height: size,
                    transform: transform3d,
                    transformStyle: 'preserve-3d',
                    transition: 'transform 0.3s ease-out',
                }}
            >
                <svg
                    width={size}
                    height={size}
                    viewBox={`0 0 ${size} ${size}`}
                >
                    {/* Compass ring */}
                    <circle
                        cx={cx} cy={cy}
                        r={size / 2 - 14}
                        fill="none"
                        stroke="rgba(255,255,255,0.1)"
                        strokeWidth="1"
                    />

                    {/* Tick marks */}
                    {ticks.map((tick, i) => (
                        <line
                            key={i}
                            x1={tick.x1} y1={tick.y1}
                            x2={tick.x2} y2={tick.y2}
                            stroke={tick.isMajor ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.15)'}
                            strokeWidth={tick.isMajor ? 1.5 : 0.5}
                        />
                    ))}

                    {/* Compass labels */}
                    {compassPoints.map((pt) => (
                        <text
                            key={pt.label}
                            x={pt.x}
                            y={pt.y}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fill={pt.isNorth ? '#ef4444' : 'rgba(255,255,255,0.4)'}
                            fontSize="10"
                            fontWeight={pt.isNorth ? '700' : '500'}
                            fontFamily="Inter, sans-serif"
                        >
                            {pt.label}
                        </text>
                    ))}

                    {/* Water surface line (animated with roll) */}
                    <line
                        x1={cx - 35 * boatScale}
                        y1={waterY + Math.sin(waterAngle * Math.PI / 180) * 20 * boatScale}
                        x2={cx + 35 * boatScale}
                        y2={waterY - Math.sin(waterAngle * Math.PI / 180) * 20 * boatScale}
                        stroke="rgba(56, 189, 248, 0.2)"
                        strokeWidth="20"
                        strokeLinecap="round"
                    />

                    {/* Boat hull */}
                    <path
                        d={hullPath}
                        fill="rgba(255,255,255,0.08)"
                        stroke="rgba(255,255,255,0.5)"
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                    />

                    {/* Keel line */}
                    <path
                        d={keelPath}
                        fill="none"
                        stroke="rgba(255,255,255,0.15)"
                        strokeWidth="0.5"
                        strokeDasharray="2 2"
                    />

                    {/* Bow marker (red triangle at front) */}
                    <polygon
                        points={`${cx},${cy - 28 * boatScale} ${cx - 3 * boatScale},${cy - 22 * boatScale} ${cx + 3 * boatScale},${cy - 22 * boatScale}`}
                        fill="rgba(239, 68, 68, 0.7)"
                    />

                    {/* Heading "needle" — north reference at top */}
                    <line
                        x1={cx} y1={2}
                        x2={cx} y2={14}
                        stroke="#ef4444"
                        strokeWidth="2"
                        strokeLinecap="round"
                    />
                </svg>
            </div>

            {/* Readouts */}
            <div className="flex gap-3 text-xs font-mono">
                <span className="text-white/50">
                    HDG <span className="text-white/80 font-semibold">{heading.toFixed(0)}°</span>
                </span>
                <span className="text-white/50">
                    PIT <span className="text-white/80 font-semibold">{pitch.toFixed(1)}°</span>
                </span>
                <span className="text-white/50">
                    ROL <span className="text-white/80 font-semibold">{roll.toFixed(1)}°</span>
                </span>
            </div>
        </div>
    )
}
